import React, { useEffect, useRef } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { useLocation } from "react-router-dom";
import "./MapView.scss";
import logo from "../assets/images/logo-sidebar.svg";
import helpIcon from "../assets/images/helpIcon.svg";

const STREAM_BASE = "https://streama.dlvh.site/api/v1/stream";
const STREAM_API_KEY =
  "sk_live_d66720b13c12126cb27c379207963d5203f600e40369b0a6adb4f236dbc59c88";
const POLL_INTERVAL_MS = 7000;

const MapView = () => {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const srcLat = parseFloat(params.get("srcLat"));
  const srcLng = parseFloat(params.get("srcLng"));
  const dstLat = parseFloat(params.get("dstLat"));
  const dstLng = parseFloat(params.get("dstLng"));
  const channel = params.get("channel") || "";

  const mapDivRef = useRef(null);
  const mapRef = useRef(null); // stable ref to map instance
  const daMarkerRef = useRef(null); // stable ref to DA marker
  const pollTimerRef = useRef(null);
  const isFetchingRef = useRef(false); // prevent concurrent fetches

  useEffect(() => {
    if (!window.google || !mapDivRef.current) return;

    const google = window.google;
    const srcPos = { lat: srcLat, lng: srcLng };
    const dstPos = { lat: dstLat, lng: dstLng };

    // Init map only once
    if (!mapRef.current) {
      mapRef.current = new google.maps.Map(mapDivRef.current, {
        center: srcPos,
        zoom: 7,
        mapTypeControl: false,
        fullscreenControl: true,
        streetViewControl: false,
      });
    }
    const map = mapRef.current;

    // Source marker
    new google.maps.Marker({
      position: srcPos,
      map,
      title: "Source",
      icon: {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 6,
        fillColor: "#085133",
        fillOpacity: 1,
        strokeColor: "#ffffff",
        strokeWeight: 2,
      },
    });

    // Destination marker — dark green teardrop pin
    new google.maps.Marker({
      position: dstPos,
      map,
      title: "Destination",
      icon: {
        url:
          "data:image/svg+xml;charset=UTF-8," +
          encodeURIComponent(
            `<svg xmlns="http://www.w3.org/2000/svg" width="36" height="48" viewBox="0 0 36 48">
              <path d="M18 0C8.06 0 0 8.06 0 18c0 13.5 18 30 18 30S36 31.5 36 18C36 8.06 27.94 0 18 0z" fill="#085133" stroke="#ffffff" stroke-width="2"/>
              <circle cx="18" cy="18" r="7" fill="#ffffff"/>
            </svg>`,
          ),
        scaledSize: new google.maps.Size(36, 48),
        anchor: new google.maps.Point(18, 48),
      },
    });

    // Draw driving route
    const directionsService = new google.maps.DirectionsService();
    const directionsRenderer = new google.maps.DirectionsRenderer({
      suppressMarkers: true,
      polylineOptions: { strokeColor: "#1A73E8", strokeWeight: 5 },
    });
    directionsRenderer.setMap(map);
    directionsService.route(
      {
        origin: srcPos,
        destination: dstPos,
        travelMode: google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status === google.maps.DirectionsStatus.OK) {
          directionsRenderer.setDirections(result);
        }
      },
    );

    const fetchAndUpdate = async () => {
      if (!channel || isFetchingRef.current) return;
      isFetchingRef.current = true;
      try {
        const res = await fetch(`${STREAM_BASE}/${channel}`, {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            "X-API-Key": STREAM_API_KEY,
          },
        });
        if (!res.ok) return;
        const json = await res.json();
        const data = json?.data?.data;
        if (data?.lat == null || data?.lng == null) return;

        const position = { lat: data.lat, lng: data.lng };

        if (!daMarkerRef.current) {
          daMarkerRef.current = new google.maps.Marker({
            position,
            map: mapRef.current,
            title: "Delivery Agent",
            icon: {
              url: `${window.location.origin}/icon_navigation.png`,
              scaledSize: new google.maps.Size(48, 48),
              anchor: new google.maps.Point(24, 24),
            },
            zIndex: 999,
          });
          mapRef.current.setCenter(position);
          mapRef.current.setZoom(14);
        } else {
          daMarkerRef.current.setPosition(position);
        }
      } catch (err) {
        console.error("[DA Tracker] Fetch error:", err);
      } finally {
        isFetchingRef.current = false;
      }
    };

    fetchAndUpdate();
    pollTimerRef.current = setInterval(fetchAndUpdate, POLL_INTERVAL_MS);

    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const redirectToHelp = () => {
    window.open("https://delivahere.com/contact/", "_blank");
  };

  const redirectToDownloadApp = () => {
    const androidStoreUrl = `https://play.google.com/store/apps/details?id=com.deliva.customer`;
    const iosStoreUrl = `https://apps.apple.com/in/app/delivahere/id6499013194`;
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    window.location.href = isIOS ? iosStoreUrl : androidStoreUrl;
  };

  return (
    <div className="map-view-page">
      <header className="delivery-header">
        <Container fluid>
          <Row className="align-items-center">
            <Col xs={5} md={6}>
              <div className="logo">
                <img src={logo} alt="DelivaHere" className="logo-image" />
              </div>
            </Col>
            <Col xs={7} md={6}>
              <div className="header-buttons">
                <div className="help-btn" onClick={redirectToHelp}>
                  <img src={helpIcon} alt="Help" />
                  Help
                </div>
                <button
                  className="download-btn"
                  onClick={redirectToDownloadApp}
                >
                  Download app
                </button>
              </div>
            </Col>
          </Row>
        </Container>
      </header>

      <div className="map-container" ref={mapDivRef} />
    </div>
  );
};

export default MapView;
